<template>
  <div>
    <v-card>
      <v-card-title>
        <div>
          <h3 class="headline mb-0">{{labels.premium}}</h3>
        </div>
        <v-spacer></v-spacer>
      </v-card-title>
      <table class="v-datatable v-table theme--light" v-if="premiumInfo.init === 1">
        <thead>
          <tr>
            <th class="text-xs-left">Benefit</th>
            <th class="text-xs-left">Description</th>
            <th class="text-xs-left" width="200px">Rate</th>
            <th class="text-xs-right" width="200px">Amount</th>
            <th class="text-xs-right" width="70px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="benefit in cargoForm.benefits" :key="benefit.value" class="benefitRow">
            <td>{{benefit.value}}</td>
            <td>{{benefit.desc}}</td>
            <td class="text-xs-right">
              <v-text-field type="tel" data-vv-scope="rate" v-num-mask="{decScale: 4}" v-model="benefit.rate" :error-messages="errors.collect('rate')" data-vv-name="rate" :data-vv-as="labels.rate" v-validate="'required|mask_decimal:4|mask_min_value:0|mask_max_value:100.00'" append-icon="mdi-percent" @blur="doManualRate(cargoForm.benefits)"></v-text-field>
            </td>
            <td class="text-xs-right">{{billCurr}} {{formatDisplayNumber(benefit.amt)}}</td>
            <td class="text-xs-right"></td>
          </tr>
          <tr v-if="checkAccess(['cargoQuoteShowBen']) && inclGst">
            <td colspan="5" class="inclGstAlert">
              <v-alert :value="true" type="info">
                Benefit(s) premium is inclusive of GST
              </v-alert>
            </td>
          </tr>
          <template v-if="showSummaryDetail">
            <tr class="summaryRow" key="premDue" v-if="checkAccess(['cargoQuoteShowDisc'])">
              <td colspan="3">
                <v-layout row wrap align-center>
                  <v-flex>
                    <v-btn flat color="primary" title="Hide Details" @click="showSummaryDetail = false">
                      Hide Details
                      <v-icon right>mdi-chevron-up</v-icon>
                    </v-btn>
                  </v-flex>
                  <v-flex d-flex>
                    <span class="text-xs-right">Gross Premium</span>
                  </v-flex>
                </v-layout>
              </td>
              <td class="text-xs-right">{{billCurr}}{{formatDisplayNumber(premiumInfo.premDue)}}</td>
              <td></td>
            </tr>
            <tr class="summaryRow" key="premDue" v-else>
              <td colspan="5">
                <v-layout row wrap align-center>
                  <v-flex>
                    <v-btn flat color="primary" title="Hide Details" @click="showSummaryDetail = false">
                      Hide Details
                      <v-icon right>mdi-chevron-up</v-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>
              </td>
            </tr>
            <tr class="summaryRow" key="discAmt" v-if="checkAccess(['cargoQuoteShowDisc'])">
              <td class="text-xs-right" colspan="3">Disc Amount</td>
              <td class="text-xs-right">({{formatDisplayNumber(premiumInfo.discPct)}}%) {{billCurr}} {{formatDisplayNumber(premiumInfo.discAmt)}}</td>
              <td>
                <v-icon v-if="canAdjustDisc" color="primary" @click="openShowAdjust">mdi-pencil</v-icon>
              </td>
            </tr>
            <tr class="summaryRow" key="netPremDue" v-if="checkAccess(['cargoQuoteShowDisc'])">
              <td class="text-xs-right" colspan="3">Net Premium</td>
              <td class="text-xs-right">{{billCurr}} {{formatDisplayNumber(premiumInfo.netPremDue)}}</td>
              <td></td>
            </tr>
            <tr class="summaryRow" key="gstAmt" v-if="checkAccess(['cargoQuoteShowDisc'])">
              <td class="text-xs-right" colspan="3">GST Amount</td>
              <td class="text-xs-right">({{formatDisplayNumber(premiumInfo.gstRate)}}%) {{billCurr}} {{formatDisplayNumber(premiumInfo.gstAmt)}}</td>
              <td></td>
            </tr>
            <tr class="summaryRow" key="premiumPayable">
              <td class="text-xs-right text-label" colspan="3">Premium Payable</td>
              <td class="text-xs-right strong-value">{{billCurr}} {{formatDisplayNumber(premiumInfo.premiumPayable)}}</td>
              <td></td>
            </tr>
            <tr class="breakRow" key="breakRow" v-if="checkAccess(['cargoQuoteShowComm'])">
              <td colspan="5"></td>
            </tr>
            <tr class="summaryRow" key="commGN" v-if="checkAccess(['cargoQuoteShowComm'])">
              <td class="text-xs-right" colspan="3">Commission G/N</td>
              <td class="text-xs-right">{{premiumInfo.commGN}}</td>
              <td></td>
            </tr>
            <tr class="summaryRow" key="commAmt" v-if="checkAccess(['cargoQuoteShowComm'])">
              <td class="text-xs-right" colspan="3">Commission Amount</td>
              <td class="text-xs-right">({{formatDisplayNumber(premiumInfo.commRate)}}%) {{billCurr}} {{formatDisplayNumber(premiumInfo.commAmt)}}</td>
              <td></td>
            </tr>
          </template>
          <template v-else>
            <tr class="summaryRow" key="premiumPayable2">
              <td colspan="3">
                <v-layout row wrap align-center>
                  <v-flex>
                    <v-btn flat color="primary" title="Show Details" @click="showSummaryDetail = true">
                      Details
                      <v-icon right>mdi-chevron-down</v-icon>
                    </v-btn>
                  </v-flex>
                  <v-flex d-flex>
                    <span class="text-label text-xs-right">Premium Payable</span>
                  </v-flex>
                </v-layout>
              </td>
              <td class="text-xs-right strong-value">{{billCurr}}{{formatDisplayNumber(premiumInfo.premiumPayable)}}</td>
              <td></td>
            </tr>
          </template>
        </tbody>
      </table>
      <loader v-else :height="300" :init="doCalculate" loadText="Calculating Premium. Please wait." errorText="Failed to Calculate Premium" :loading="premiumInfo.init === 0" :errorMsgs="premiumInfo.initErr"></loader>
    </v-card>
    <v-dialog v-model="showAdjust" max-width="600px" lazy>
      <v-card>
        <v-card-title>
          <span class="headline">Discount Adjustment</span>
          <v-spacer></v-spacer>
          <v-btn flat icon @click="showAdjust = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div>
            <v-layout row wrap>
              <v-flex xs2 class="pa-2">
                <span>Min: </span><br/>
                <span class="font-weight-bold">{{formatDisplayNumber(minAdj)}}</span>
              </v-flex>
              <v-flex xs8>
                <v-text-field box v-num-mask v-model="adjDiscount" label="Discount %" type="tel" :error-messages="errors.collect('adjDiscount')" data-vv-name="adjDiscount" data-vv-as="Discount %" v-validate="{ required: true, mask_decimal:2, mask_min_value: minAdj, mask_max_value: maxAdj }" v-if="showAdjust"></v-text-field>
              </v-flex>
              <v-flex xs2 class="pa-2">
                <span>Max: </span><br/>
                <span class="font-weight-bold">{{formatDisplayNumber(maxAdj)}}</span>
              </v-flex>
            </v-layout>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" flat="flat" @click.native="showAdjust = false">Cancel</v-btn>
          <v-btn color="primary" flat="flat" @click.native="doAdjustDisc">Adjust</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import clone from 'clone'
import MessageMixin from '@/mixins/message'
import { mapGetters } from 'vuex'
import { formatDisplayNumber } from '@/utils/number'
import { QUOTE_CARGO_PREMIUM_MANUALRATE, AGENCY_QUOTATION_DISCADJ } from '@/store/types'

export default {
  data() {
    return {
      labels: {
        premium: 'Premium Summary',
        rate: 'Rate'
      },
      showSummaryDetail: false,
      manualRate: [],
      cargoForm: {
        siDeclared: undefined,
        benefits: []
      },
      showAdjust: false,
      adjDiscount: undefined
    }
  },
  computed: {
    ...mapGetters([
      'checkAccess'
    ]),
    globalForm() {
      return this.$store.state.agency.quotation.cargoForm
    },
    premiumInfo() {
      return this.$store.state.agency.quotation.commonInfo.premium
    },
    siCurr() {
      return this.$store.state.agency.quotation.cargoForm.siCurr
    },
    billCurr() {
      return this.$store.state.agency.quotation.commonForm.billCurr
    },
    canAdjustDisc() {
      if (!this.premiumInfo.protDisc && this.checkAccess(['cargoQuoteDisc'])) {
        return true
      }
      return false
    },
    minAdj() {
      return 0
    },
    maxAdj() {
      if (this.premiumInfo.commGN === 'G') {
        return this.premiumInfo.commRateMax
      }
      return 100
    },
    inclGst() {
      if (this.premiumInfo.inclGst) {
        return true
      }
      return false
    }
  },
  watch: {
    'premiumInfo.init': function (value) {
      if (value === 1) {
        this.doInit()
      }
    }
  },
  methods: {
    doInit() {
      this.cargoForm.siDeclared = this.globalForm.siDeclared
      this.cargoForm.benefits = clone(this.premiumInfo.benefits)
    },
    doCalculate() {
      this.$store.dispatch('cargoQuoteCalculate')
    },
    doManualRate(benefits) {
      this.$validator.validateAll('rate').then(result => {
        if (result) {
          this.manualRate = []
          for (let benefit of benefits) {
            this.manualRate.push({
              key: benefit.value,
              value: benefit.rate
            })
          }
          this.$store.commit(QUOTE_CARGO_PREMIUM_MANUALRATE, this.manualRate)
          this.doCalculate()
        }
      })
    },
    formatDisplayNumber(num) {
      return formatDisplayNumber(num)
    },
    openShowAdjust() {
      // Assign latest disc % to local state instead of adjDiscount
      // Reason - discount adjustment control is done during calculation, any error will rollback to default (no adjustment)
      // therefore adjDiscount in vuex might not always be accurate
      this.adjDiscount = this.premiumInfo.discPct
      this.showAdjust = true
    },
    doAdjustDisc() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          this.$store.commit(AGENCY_QUOTATION_DISCADJ, this.adjDiscount)
          this.showAdjust = false
          // Do a recalculate
          this.doCalculate()
        }
      })
    }
  },
  mounted() {
    this.doInit()
  },
  mixins: [
    MessageMixin
  ]
}
</script>

<style lang="scss">
.summaryRow {
  background: #eee;
  .text-label {
    font-weight: 600;
  }
  .strong-value {
    font-weight: 600;
  }
}
.breakRow {
  &:hover {
    background: inherit !important;
  }
  td {
    height: 15px !important;
  }
}
.inclGstAlert {
  .v-alert {
    margin: 0 !important;
  }
}
td.inclGstAlert{
  padding: 0 !important;
}
</style>
